import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from "../components/LoadingSpinner";
import '../css/characters.css';
import api from "../api";

const CharactersPage = () => {
    const [ActiveData, setActiveData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [selectedCharacterId, setSelectedCharacterId] = useState(0);
    const [characters, setCharacters] = useState([]);
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);
    const charactersWrapperRef = useRef(null);
    const heroRef = useRef(null);
    const { t, i18n } = useTranslation();
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const [searchParams] = useSearchParams();
    const requestedCharacterId = searchParams.get('id');

    const fetchCharacter = async (characterId, language = i18n.language) => {
        setLoading(true);
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/characterInfo?id=${characterId}&lang=${language}`);
            setActiveData(response.data);
            setSelectedCharacterId(characterId);
        } catch (error) {
            console.error('Error fetching character details:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        const fetchCharactersList = async () => {
            setLoadingPage(true);
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/api/characters/list`);
                setCharacters(response.data);
                const requested = requestedCharacterId
                    ? response.data.find((c) => String(c.id) === String(requestedCharacterId))
                    : null;
                const initialId = requested
                    ? requested.id
                    : response.data[Math.floor(Math.random() * response.data.length)]?.id;
                if (initialId) {
                    setSelectedCharacterId(initialId);
                    await fetchCharacter(initialId);
                }
            } catch (error) {
                console.error('Error fetching characters list:', error);
                setCharacters([]);
            }
            setLoadingPage(false);
        };
        fetchCharactersList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (requestedCharacterId && characters.length > 0) {
            const id = Number(requestedCharacterId);
            const exists = characters.some((c) => c.id === id);
            if (exists && id !== selectedCharacterId) {
                fetchCharacter(id);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedCharacterId, characters, selectedCharacterId]);

    useEffect(() => {
        const handleLanguageChange = (lng) => {
            fetchCharacter(selectedCharacterId, lng);
        };
        i18n.on('languageChanged', handleLanguageChange);
        return () => i18n.off('languageChanged', handleLanguageChange);
    }, [selectedCharacterId, i18n]);

    useEffect(() => {
        if (charactersWrapperRef.current && selectedCharacterId !== 0) {
            const activeElement = charactersWrapperRef.current.querySelector(`.char-avatar.active`);
            if (activeElement) {
                activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [selectedCharacterId]);

    useEffect(() => {
        const wrapper = charactersWrapperRef.current;
        if (wrapper && characters.length > 0 && !loadingPage) {
            const handleMouseDown = (e) => {
                isDraggingRef.current = true;
                hasMovedRef.current = false;
                startXRef.current = e.pageX - wrapper.offsetLeft;
                scrollLeftRef.current = wrapper.scrollLeft;
                wrapper.classList.add('dragging');
            };

            const handleMouseLeaveOrUp = () => {
                if (isDraggingRef.current) {
                    isDraggingRef.current = false;
                }
                wrapper.classList.remove('dragging');
            };

            const handleMouseMove = (e) => {
                if (!isDraggingRef.current) return;
                e.preventDefault();
                const x = e.pageX - wrapper.offsetLeft;
                const walk = x - startXRef.current;
                if (Math.abs(walk) > 5) {
                    hasMovedRef.current = true;
                }
                wrapper.scrollLeft = scrollLeftRef.current - walk;
            };

            wrapper.addEventListener('mousedown', handleMouseDown);
            wrapper.addEventListener('mouseleave', handleMouseLeaveOrUp);
            wrapper.addEventListener('mouseup', handleMouseLeaveOrUp);
            wrapper.addEventListener('mousemove', handleMouseMove);

            return () => {
                wrapper.removeEventListener('mousedown', handleMouseDown);
                wrapper.removeEventListener('mouseleave', handleMouseLeaveOrUp);
                wrapper.removeEventListener('mouseup', handleMouseLeaveOrUp);
                wrapper.removeEventListener('mousemove', handleMouseMove);
            };
        }
    }, [characters, loadingPage]);

    const handleCharacterClick = (characterId) => {
        if (characterId === selectedCharacterId || isDraggingRef.current || hasMovedRef.current) {
            return;
        }
        setSelectedCharacterId(characterId);
        fetchCharacter(characterId);
    };

    if (loadingPage) {
        return <LoadingSpinner />;
    }

    return (
        <div className="characters-page">
            <nav className="characters-rail">
                <div className="characters-rail-inner" ref={charactersWrapperRef}>
                    {characters.map((character) => {
                        const isActive = selectedCharacterId === character.id;
                        return (
                            <button
                                key={character.id}
                                className={`char-avatar ${isActive ? 'active' : ''}`}
                                onClick={() => handleCharacterClick(character.id)}
                                aria-label={character.name}
                            >
                                <span className="char-avatar-ring">
                                    <img
                                        src={character.logo_url}
                                        alt={character.name}
                                        className={isActive ? '' : 'grayscale_logo'}
                                        draggable="false"
                                        onDragStart={(e) => e.preventDefault()}
                                    />
                                </span>
                                <span className="char-avatar-name">{t(`characters.heroes.${character.name}`)}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            <main className="character-focus">
                {loading || !ActiveData ? (
                    <LoadingSpinner className="character-loading" />
                ) : (
                    <>
                        <section className="character-hero" key={selectedCharacterId} ref={heroRef}>
                            <div className="character-hero-info">
                                <span className="sp-eyebrow">{t('characters.title')}</span>
                                <h1 className="character-hero-name">{t(`characters.heroes.${ActiveData.character}`)}</h1>
                                <p className="character-hero-desc">{ActiveData.description}</p>

                                <div className="character-ultimate">
                                    <span className="ultimate-eyebrow">{t('characters.ultimate')}</span>
                                    <h3 className="ultimate-name">{ActiveData.ultimate_name}</h3>
                                    <p className="ultimate-description">{ActiveData.ultimate_description}</p>
                                </div>

                                {ActiveData.specialized_weapons && (
                                    <div className="character-weapons">
                                        <span className="weapons-label">{t('characters.specialized_weapons')}</span>
                                        <div className="weapons-chips">
                                            {ActiveData.specialized_weapons.split(',').map((weapon, index) => (
                                                <span key={index} className="weapon-chip">{weapon.trim()}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="character-hero-art">
                                <div className="character-hero-glow" />
                                {ActiveData.image_url && (
                                    <img src={ActiveData.image_url} alt={ActiveData.character} className="character-hero-image" />
                                )}
                            </div>
                        </section>

                        <section className="character-skills">
                            <div className="skills-head">
                                <span className="sp-eyebrow">{t('characters.skills')}</span>
                            </div>
                            <div className="skills-grid">
                                {['red', 'green', 'blue'].map((type) => {
                                    const skills = ActiveData.skills.filter((skill) => skill.type === type);
                                    return (
                                        <div key={type} className="skill-group">
                                            <div className={`skill-group-head gh-${type}-cell`}>
                                                {t(`skills.${type}`)}
                                            </div>
                                            <div className="skill-list">
                                                {skills.map((skill) => (
                                                    <div key={skill.name} className="skill">
                                                        <h4 className="skill-name">{skill.name}</h4>
                                                        <ul className="skill-levels">
                                                            {[1, 2, 3].map((level) => {
                                                                const levelKey = `level${level}`;
                                                                if (!skill[levelKey]) return null;
                                                                return (
                                                                    <li key={levelKey}>
                                                                        <span className="skill-level-num">{level}</span>
                                                                        <span className="skill-level-text">{skill[levelKey]}</span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default CharactersPage;
